/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, takeUntil } from "rxjs";

import { ConfirmationService, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { Popover, PopoverModule } from "primeng/popover";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

import { SessionService } from "../../../../core/services/session.service";
import {
  QPilotAccessToken,
  QPilotUser,
} from "../../../../core/services/qpilot.service";

import {
  SurveyFilterParams,
  SurveyListItem,
} from "../../interfaces/retention-workflow.interface";
import { RetentionWorkflowService } from "../../services/retention-workflow.service";
import {
  RETENTION_WORKFLOWS_DOCS,
  SURVEY_TOAST_MESSAGES,
  SURVEY_CONFIRM_MESSAGES,
  LEARN_MORE_CONTENT,
  MAX_WORKFLOWS,
  MAX_WORKFLOWS_MESSAGE,
} from "../../constants/retention-workflow.constants";

import {
  RetentionWorkflowsFiltersComponent,
  RetentionWorkflowsFilterForm,
} from "../../components/retention-workflows-filters/retention-workflows-filters.component";
import { RetentionWorkflowsTableComponent } from "../../components/retention-workflows-table/retention-workflows-table.component";
import { ListPageSkeletonComponent } from "../../../../shared/components/table-skeleton";

@Component({
  selector: "app-retention-workflows-list",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    ConfirmDialogModule,
    PopoverModule,
    ToastModule,
    TooltipModule,
    RetentionWorkflowsFiltersComponent,
    RetentionWorkflowsTableComponent,
    ListPageSkeletonComponent,
  ],
  templateUrl: "./retention-workflows-list.component.html",
  styleUrl: "./retention-workflows-list.component.scss",
  providers: [ConfirmationService],
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RetentionWorkflowsListComponent implements OnInit, OnDestroy {
  @ViewChild("learnMorePopover") learnMorePopover!: Popover;

  // Input properties for embedded mode
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() embeddedMode: boolean = false;
  @Input() hideCreateButton: boolean = false;
  @Input() hideToolbar: boolean = false;

  private readonly destroy$ = new Subject<void>();

  // State signals
  loading = signal(true);
  tableLoading = signal(false);
  surveys = signal<SurveyListItem[]>([]);
  totalRecords = signal(0);
  // UI state - will be initialized based on screen size
  sidebarCollapsed = signal(false);
  private userManuallyCollapsed = false; // Track if user manually toggled sidebar

  // Site data
  siteId: number | null = null;

  // Filter form
  filterForm = new FormGroup<RetentionWorkflowsFilterForm>({
    status: new FormControl<string[]>([], { nonNullable: true }),
    actionTrigger: new FormControl<number[]>([], { nonNullable: true }),
  });

  // Learn more content
  readonly learnMoreContent = LEARN_MORE_CONTENT;
  readonly docsUrl = RETENTION_WORKFLOWS_DOCS.main;

  // Max workflows
  readonly maxWorkflows = MAX_WORKFLOWS;
  readonly maxWorkflowsMessage = MAX_WORKFLOWS_MESSAGE;
  maxWorkflowsReached = signal(false);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly retentionService: RetentionWorkflowService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.initializeResponsiveSidebar();
    this.setupWindowResizeListener();
    // Handle embedded mode inputs (web component / iframe)
    const element = this.elementRef.nativeElement as HTMLElement;

    // Check HTML attribute for embedded mode (web component mode)
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");

    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get siteId from input/attribute
    const siteIdString =
      this.inputSiteId?.toString() ??
      element.getAttribute("input-site-id") ??
      element.getAttribute("inputSiteId");

    if (siteIdString) {
      this.siteId = parseInt(siteIdString, 10);
    }

    // Get token from attribute and convert
    const tokenString =
      element.getAttribute("input-token") ?? element.getAttribute("inputToken");

    // Create inputToken from attribute if:
    // 1. We have a token string from attribute
    // 2. AND inputToken is either missing, not an object, or doesn't have valid TokenBearerAuth
    // Note: Angular Elements may bind the attribute as a raw string, so we check for proper structure
    if (
      tokenString &&
      (!this.inputToken ||
        typeof this.inputToken !== "object" ||
        !this.inputToken.TokenBearerAuth)
    ) {
      this.inputToken = {
        TokenBearerAuth: tokenString,
        TokenAuth: "",
        Id: 0,
        UserId: 0,
        CreatedUtc: new Date(),
        UpdatedUtc: new Date(),
        ExpiresUtc: new Date(),
        User: {} as QPilotUser,
        FirstLogin: 0,
      };
    }

    // Initialize based on mode
    if (this.embeddedMode && this.inputToken && this.siteId) {
      this.loadSurveys(true); // Initial load shows skeleton
    } else if (!this.embeddedMode) {
      // Standard mode: Get siteId from parent route (widgets/dashboard/:siteId)
      this.route.parent?.paramMap
        .pipe(takeUntil(this.destroy$))
        .subscribe((params) => {
          const siteIdParam = params.get("siteId");
          if (siteIdParam) {
            this.siteId = parseInt(siteIdParam, 10);
            this.loadSurveys(true); // Initial load shows skeleton
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  loadSurveys(isInitialLoad = false): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !accessToken) return;

    // Only show full page skeleton on initial load
    if (isInitialLoad) {
      this.loading.set(true);
    } else {
      this.tableLoading.set(true);
    }

    const filters = this.buildFilters();

    this.retentionService
      .getSurveys(this.siteId, accessToken, filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const surveys = response.result?.surveys || [];
          this.surveys.set(surveys);
          this.totalRecords.set(surveys.length);
          this.maxWorkflowsReached.set(surveys.length >= MAX_WORKFLOWS);
          this.loading.set(false);
          this.tableLoading.set(false);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error("Error loading surveys:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: SURVEY_TOAST_MESSAGES.loadError,
          });
          this.loading.set(false);
          this.tableLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  private buildFilters(): SurveyFilterParams {
    const formValue = this.filterForm.value;
    const filters: SurveyFilterParams = {};

    // Status filters
    if (formValue.status?.includes("enabled")) {
      filters.Enabled = true;
    }
    if (formValue.status?.includes("deleted")) {
      filters.Deleted = true;
    }

    // Action trigger filter (support multiple values like old QMC)
    if (formValue.actionTrigger && formValue.actionTrigger.length > 0) {
      filters.ActionTrigger = formValue.actionTrigger;
    }

    return filters;
  }

  onFiltersChanged(): void {
    this.loadSurveys();
  }

  clearFilters(): void {
    this.filterForm.reset({
      status: [],
      actionTrigger: [],
    });
    this.loadSurveys();
  }

  hasActiveFilters(): boolean {
    const formValue = this.filterForm.value;
    return (
      (formValue.status?.length || 0) > 0 ||
      (formValue.actionTrigger?.length || 0) > 0
    );
  }

  toggleSidebar(): void {
    const newState = !this.sidebarCollapsed();
    this.sidebarCollapsed.set(newState);
    this.userManuallyCollapsed = newState; // Track user preference
  }

  toggleLearnMorePopover(event: Event): void {
    this.learnMorePopover.toggle(event);
  }

  openDocs(): void {
    window.open(this.docsUrl, "_blank");
  }

  navigateToCreate(): void {
    if (!this.siteId) return;

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "retention-workflows-list",
          action: "create",
          siteId: this.siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate([
        "/widgets/dashboard",
        this.siteId,
        "retain-and-grow",
        "retention",
        "create",
      ]);
    }
  }

  editSurvey(item: SurveyListItem): void {
    if (!this.siteId) return;

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "retention-workflows-list",
          action: "edit",
          workflowId: item.survey.id,
          siteId: this.siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate([
        "/widgets/dashboard",
        this.siteId,
        "retain-and-grow",
        "retention",
        item.survey.id,
        "edit",
      ]);
    }
  }

  viewSurvey(item: SurveyListItem): void {
    if (!this.siteId) return;

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "retention-workflows-list",
          action: "view",
          workflowId: item.survey.id,
          siteId: this.siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate([
        "/widgets/dashboard",
        this.siteId,
        "retain-and-grow",
        "retention",
        item.survey.id,
        "view",
      ]);
    }
  }

  deleteSurvey(item: SurveyListItem): void {
    this.confirmationService.confirm({
      header: SURVEY_CONFIRM_MESSAGES.delete.header,
      message: SURVEY_CONFIRM_MESSAGES.delete.message,
      acceptLabel: SURVEY_CONFIRM_MESSAGES.delete.acceptLabel,
      rejectLabel: SURVEY_CONFIRM_MESSAGES.delete.rejectLabel,
      acceptButtonStyleClass: "p-button-danger",
      accept: () => {
        this.performDelete(item);
      },
    });
  }

  private performDelete(item: SurveyListItem): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !accessToken) return;

    this.tableLoading.set(true);

    this.retentionService
      .deleteSurvey(this.siteId, item.survey.id, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: SURVEY_TOAST_MESSAGES.deleteSuccess,
          });
          this.loadSurveys();
        },
        error: (error) => {
          console.error("Error deleting survey:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: SURVEY_TOAST_MESSAGES.deleteError,
          });
          this.tableLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  duplicateSurvey(item: SurveyListItem): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !accessToken) return;

    // Check if max workflows reached before attempting duplicate
    if (this.maxWorkflowsReached()) {
      this.messageService.add({
        severity: "warn",
        summary: this.maxWorkflowsMessage.title,
        detail: SURVEY_TOAST_MESSAGES.maxWorkflowsReached,
      });
      return;
    }

    this.tableLoading.set(true);

    this.retentionService
      .duplicateSurvey(this.siteId, item.survey.id, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: SURVEY_TOAST_MESSAGES.duplicateSuccess,
          });
          // Always refresh the list to show the new duplicate
          this.loadSurveys();
        },
        error: (error) => {
          console.error("Error duplicating survey:", error);
          // Check if error is about max workflows
          const errorMessage =
            typeof error?.error === "string" &&
            error.error.includes("10 surveys")
              ? SURVEY_TOAST_MESSAGES.maxWorkflowsReached
              : SURVEY_TOAST_MESSAGES.duplicateError;
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: errorMessage,
          });
          this.tableLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  toggleEnabled(item: SurveyListItem): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !accessToken) return;

    const action = item.survey.isEnabled
      ? this.retentionService.enableSurvey(
          this.siteId,
          item.survey.id,
          accessToken,
        )
      : this.retentionService.disableSurvey(
          this.siteId,
          item.survey.id,
          accessToken,
        );

    const successMessage = item.survey.isEnabled
      ? SURVEY_TOAST_MESSAGES.enableSuccess
      : SURVEY_TOAST_MESSAGES.disableSuccess;
    const errorMessage = item.survey.isEnabled
      ? SURVEY_TOAST_MESSAGES.enableError
      : SURVEY_TOAST_MESSAGES.disableError;

    action.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        if (response.warning) {
          this.messageService.add({
            severity: "warn",
            summary: "Warning",
            detail: response.warning,
          });
        } else {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: successMessage,
          });
        }
        this.loadSurveys();
      },
      error: (error) => {
        console.error("Error toggling survey enabled state:", error);
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
        });
        // Revert the toggle state
        item.survey.isEnabled = !item.survey.isEnabled;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Initialize sidebar state based on screen size
   */
  private initializeResponsiveSidebar(): void {
    const isMobile = window.innerWidth <= 768;
    this.sidebarCollapsed.set(isMobile);
  }

  /**
   * Setup window resize listener for responsive behavior
   * Auto-collapses on mobile and auto-expands on desktop (unless user manually toggled)
   */
  private setupWindowResizeListener(): void {
    const resizeObserver = new ResizeObserver(() => {
      const isMobile = window.innerWidth <= 768;

      // Only auto-collapse on mobile if user hasn't manually set preference
      if (isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(true);
      } else if (!isMobile && !this.userManuallyCollapsed) {
        // Only auto-expand on desktop if user hasn't manually collapsed
        this.sidebarCollapsed.set(false);
      }
      // If user manually toggled, respect their preference regardless of screen size

      this.cdr.markForCheck();
    });

    // Observe the window for resize changes
    resizeObserver.observe(document.body);

    // Clean up observer on destroy
    this.destroy$.subscribe(() => {
      resizeObserver.disconnect();
    });
  }
}
